/* @flow */
/* eslint-env es6,browser */

import * as u from '../utils';
import bidbase from './bidbase';
import bm from '../bidmanager';

/**
 * @module bidders/a9v2
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=amz_bid_v2]
 * @property {string} id
 */
/**
 * @description Implements functionality for amazon (aps) header bidding
 * @exports a9v2bid
 * @extends _bidbase
 * @requires module:utils
 * @requires module:adloader
 */

class _a9v2bid extends bidbase {
  _config: a9v2Config;
  _DefConfig: a9v2Config;
  constructor() {
    super('a9v2');

    this._DefConfig = u.extend(true, {}, this._DefConfig, {
      pubID: '',
      qpKey: 'amz_bid_v2',
      bucketMap: {}
    });
    this._config = u.extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['script', 'pubID'];
    this._renderType = 'func';
    this._initCheck = {
      context: window,
      obj: 'apstag'
    };
    this._enableReporting = false;
  }

  _postInit(): initStatus {
    window.apstag.init({
      pubID: this._config.pubID,
      bidTimeout: this._config.timeout
    });
    this._renderFunc = window.apstag.renderImp;
    return u.F_INITED;
  }

  /**
   *
   * @param {string} priceCode - price bucket code
   * @returns {number} - price in CPM for the bucket
   */
  getBidFromBucketMap(priceCode: string | number): string {
    priceCode = String(priceCode);
    return this._config.bucketMap[priceCode] || '0';
  }

  /**
   * Process bid response. Response Format is array of objects
   * with each object's props as slotID , amznbid , amzniid , amznsz , amznp
   * @param {Array<string>} posList - array of positions
   * @param {Array<Object>} res -  response object
   * @param {Array<string>} keys - slot response keys
   * @param {Function} resolve -  resolve function
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {void} - No Return
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    res: Array<Object>,
    keys: Array<string>,
    resolve: Function,
    ret: hbPartnerData
  ) {
    //List of keys returned by Amazon for each slot in case of successful bids
    //slotID is available by default
    if (!(res.length && keys.includes('amznbid') && keys.includes('amzniid'))) {
      resolve(ret);
      return;
    }

    res.forEach(slot => {
      this._handleBidPosData(ret, slot.slotID, slot.amznbid, slot.amzniid);
    });
    resolve(ret);
  }

  /**
   * Overriden base class function to prevent failure
   * @param {string} pos - Position name
   * @returns {string} - '12345'
   */
  /* eslint-disable no-unused-vars */
  getLineId(pos: string): string {
    /* eslint-enable no-unused-vars */
    //lineId is not needed here so send some random value
    return '12345';
  }

  /**
   * Create slot object for an ad position
   * @param {string} pos - Ad position
   * @returns {Object} - Request URL info for given position
   * @access private
   */
  _getSlot(
    pos: string
  ): { slotID: string, slotName: string, sizes: Array<[number, number]> } {
    let params = {
      slotID: pos,
      slotName: this._config.posTokenMap[pos].slotName,
      sizes: this._config.posTokenMap[pos].sizes
    };
    return params;
  }

  /**
   * get bids for given positions
   * @param {Array<string>} posList - HB Position list
   * @param {Object.<string, hbPosData>} ret - Return data
   * @return {Promise<Object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return new Promise(resolve =>
      window.apstag.fetchBids(
        {
          slots: posList.map(pos => this._getSlot(pos))
        },
        bids =>
          this._processBidResponse(
            posList,
            bids,
            window.apstag.targetingKeys(),
            resolve,
            ret
          )
      )
    );
  }
}
//add bidder to bidmanager
const a9v2bid = new _a9v2bid();
bm.addBidder('a9v2', a9v2bid);
export default a9v2bid;
